/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';

import { ping } from '../communicator/communicator';

/**
 * @import { IServerConnectionMonitor, ServerConnectionMonitorEventTypes } from '.'
 * @import { ServerDeviceId } from '../session/session-attrs'
 */

/** Interval mezi jednotlivými pokusy o ping serveru [ms]. */
const pingRetryInterval = 60000;

/** Časový limit pro ping serveru [ms]. */
const pingTimeout = 3000;

// TODO: Šlo by pridať do DI Communicator a zoptimalizovať ping servera
// načúvaním na event 'heartbeatFetched'.
// Tam môže robiť problém to, že nevieme, od akého serverUrl heartbeat info prišlo.
/**
 * Třída pro sledování připojení k serveru.
 * @implements {IServerConnectionMonitor}
 */
export default class ServerConnectionMonitor {
  /**
   * @param {string | null} serverUrl
   */
  constructor(serverUrl) {
    /** @private @type {EventEmitter<ServerConnectionMonitorEventTypes>} */
    this.emitter = new EventEmitter();
    this.serverUrl = serverUrl;
    this.connected = false;
    this.timeout = undefined;
  }

  /** @type {IServerConnectionMonitor['getServerInfo']} */
  getServerInfo() {
    return {
      serverUrl: this.serverUrl,
      connected: this.connected
    };
  }

  /** @type {IServerConnectionMonitor['destroy']} */
  destroy() {
    this.stop();
    this.emitter.removeAllListeners();
  }

  /** @type {IServerConnectionMonitor['off']} */
  off(...args) {
    this.emitter.off(...args);
    // Pokud už není žádný posluchač, tak zastaví pingování.
    if (this.emitter.listenerCount('connectionChanged') === 0) {
      this.stop();
    }
  }

  /** @type {IServerConnectionMonitor['on']} */
  on(...args) {
    this.emitter.on(...args);
    if (this.timeout === undefined) {
      this.start();
    }
  }

  /**
   * @private
   */
  async onTimeout() {
    if (!this.serverUrl) {
      return;
    }
    const connected = await ping(this.serverUrl, pingTimeout);
    if (this.connected !== connected) {
      this.connected = connected;
      this.emitter.emit('connectionChanged', {
        connected,
        serverUrl: this.serverUrl
      });
    }
    this.timeout = setTimeout(this.onTimeout.bind(this), pingRetryInterval);
  }

  // TODO: Malo by byť nejak zabezpečené?
  // 1. Mala by byť len jedna autorita, ktorá nastavuje serverUrl.
  // 2. Napočúvať môžu viacerí.
  // 3. Nedáva zmysel pingovať viacero serverov naraz.
  // Ak by sa mala táto metóda odstrániť, tak to by znamenalo, že
  // - malo by existovať viacero inštancií ServerConnectionMonitor, každá s iným serverUrl
  // - alebo by sa viazal typ eventu k serverUrl
  // Obe možnosti idú proti bodu 3.
  /**
   * @param {ServerDeviceId['serverUrl']} serverUrl
   */
  setServerUrl(serverUrl) {
    if (this.serverUrl !== serverUrl) {
      this.serverUrl = serverUrl;
      this.start();
    }
  }

  /**
   * @private
   */
  start() {
    this.stop();
    this.onTimeout();
  }

  /**
   * @private
   */
  stop() {
    clearTimeout(this.timeout);
    this.timeout = undefined;
  }
}

export const internal = { pingRetryInterval, pingTimeout };
